import { matchPath } from 'react-router-dom';
import type { TFunction } from 'i18next';

type TitleRule = {
  path: string;
  title: (params: Record<string, string | undefined>, t: TFunction) => string;
};

const ROUTE_TITLE_RULES: TitleRule[] = [
  {
    path: '/classrooms/:code/students/:studentId/submissions',
    title: (_p, t) => t('documentTitle.routes.studentSubmissions'),
  },
  {
    path: '/classrooms/:code/students/names',
    title: (_p, t) => t('documentTitle.routes.studentNames'),
  },
  {
    path: '/classrooms/:code/sessions/detail',
    title: (_p, t) => t('documentTitle.routes.sessionDetail'),
  },
  {
    path: '/classrooms/:code/assignments/create',
    title: (_p, t) => t('documentTitle.routes.assignmentCreate'),
  },
  {
    path: '/classrooms/:code/assignments/:assignmentCode',
    title: (_p, t) => t('documentTitle.routes.assignmentDetail'),
  },
  {
    path: '/classrooms/:code',
    title: (_p, t) => t('documentTitle.routes.classroom'),
  },
  {
    path: '/classrooms/create',
    title: (_p, t) => t('documentTitle.routes.classroomCreate'),
  },
  {
    path: '/classrooms',
    title: (_p, t) => t('documentTitle.routes.classrooms'),
  },
  {
    path: '/assignments/:code/students/:studentId/practice',
    title: (_p, t) => t('documentTitle.routes.studentPractice'),
  },
  {
    path: '/assignments/:code/items/:itemId',
    title: (_p, t) => t('documentTitle.routes.assignmentItem'),
  },
  {
    path: '/assignments/:code/edit',
    title: (_p, t) => t('documentTitle.routes.assignmentEdit'),
  },
  {
    path: '/assignments/:code',
    title: (_p, t) => t('documentTitle.routes.assignmentDetail'),
  },
  {
    path: '/assignments/create',
    title: (_p, t) => t('documentTitle.routes.assignmentCreate'),
  },
  {
    path: '/exercise-library/groups/:groupId/templates/:templateId',
    title: (_p, t) => t('documentTitle.routes.templateDetail'),
  },
  {
    path: '/exercise-library/templates/:templateId',
    title: (_p, t) => t('documentTitle.routes.templateDetail'),
  },
  {
    path: '/exercise-library/tab/:tab',
    title: (_p, t) => t('documentTitle.routes.exerciseLibrary'),
  },
  {
    path: '/exercise-library/:groupId',
    title: (_p, t) => t('documentTitle.routes.exerciseLibrary'),
  },
  {
    path: '/exercise-library',
    title: (_p, t) => t('documentTitle.routes.exerciseLibrary'),
  },
  {
    path: '/preview/practice/:templateId',
    title: (_p, t) => t('documentTitle.routes.previewPractice'),
  },
  {
    path: '/content/sources/:id',
    title: (_p, t) => t('documentTitle.routes.contentSourceDetail'),
  },
  {
    path: '/content/sources',
    title: (_p, t) => t('documentTitle.routes.contentSources'),
  },
  {
    path: '/content/activities',
    title: (_p, t) => t('documentTitle.routes.contentActivities'),
  },
  {
    path: '/indexed-documents',
    title: (_p, t) => t('documentTitle.routes.indexedDocuments'),
  },
  {
    path: '/student/assignments/:code/worksheet/submissions/:submissionId',
    title: (_p, t) => t('documentTitle.routes.worksheetReview'),
  },
  {
    path: '/student/assignments/:code/worksheet',
    title: (_p, t) => t('documentTitle.routes.worksheet'),
  },
  {
    path: '/student/assignments/:code/practice',
    title: (_p, t) => t('documentTitle.routes.studentPracticePage'),
  },
  {
    path: '/student/assignments/:code/submission',
    title: (_p, t) => t('documentTitle.routes.studentSubmission'),
  },
  {
    path: '/student/assignments/:code',
    title: (_p, t) => t('documentTitle.routes.studentAssignment'),
  },
  {
    path: '/my-classrooms/:code',
    title: (_p, t) => t('documentTitle.routes.studentClassroom'),
  },
  {
    path: '/my-classrooms',
    title: (_p, t) => t('documentTitle.routes.myClassrooms'),
  },
  {
    path: '/public-resources',
    title: (_p, t) => t('documentTitle.routes.publicResources'),
  },
  {
    path: '/students/:studentId/classrooms',
    title: (_p, t) => t('documentTitle.routes.studentClassrooms'),
  },
  {
    path: '/students/progress',
    title: (_p, t) => t('documentTitle.routes.studentsProgress'),
  },
  {
    path: '/students',
    title: (_p, t) => t('documentTitle.routes.students'),
  },
  {
    path: '/users/:id/dashboard',
    title: (_p, t) => t('documentTitle.routes.userDashboard'),
  },
  {
    path: '/users/:id',
    title: (_p, t) => t('documentTitle.routes.userDetail'),
  },
  {
    path: '/admin/users/:id/dashboard',
    title: (_p, t) => t('documentTitle.routes.userDashboard'),
  },
  {
    path: '/admin/users/:id',
    title: (_p, t) => t('documentTitle.routes.userDetail'),
  },
  {
    path: '/admin/users/account-links/:groupId',
    title: (_p, t) => t('documentTitle.routes.adminAccountLinkDetail'),
  },
  {
    path: '/admin/users/account-links',
    title: (_p, t) => t('documentTitle.routes.adminAccountLinks'),
  },
  {
    path: '/admin/fe-telemetry-logs',
    title: (_p, t) => t('documentTitle.routes.feTelemetry'),
  },
  {
    path: '/admin/error-logs',
    title: (_p, t) => t('documentTitle.routes.errorLogs'),
  },
  {
    path: '/admin/audit-logs',
    title: (_p, t) => t('documentTitle.routes.auditLogs'),
  },
  {
    path: '/admin/tts-audio-cache',
    title: (_p, t) => t('documentTitle.routes.adminTtsCache'),
  },
  {
    path: '/admin/materialized-views',
    title: (_p, t) => t('documentTitle.routes.adminMaterializedViews'),
  },
  {
    path: '/admin/ai-config',
    title: (_p, t) => t('documentTitle.routes.adminAiConfig'),
  },
  {
    path: '/admin/system',
    title: (_p, t) => t('documentTitle.routes.adminSystem'),
  },
  {
    path: '/admin/roles',
    title: (_p, t) => t('documentTitle.routes.adminRoles'),
  },
  {
    path: '/admin',
    title: (_p, t) => t('documentTitle.routes.adminDashboard'),
  },
  {
    path: '/notifications',
    title: (_p, t) => t('documentTitle.routes.notifications'),
  },
  {
    path: '/settings',
    title: (_p, t) => t('documentTitle.routes.settings'),
  },
  {
    path: '/profile',
    title: (_p, t) => t('profile.title'),
  },
  {
    path: '/forbidden',
    title: (_p, t) => t('documentTitle.routes.forbidden'),
  },
  {
    path: '/login-local',
    title: (_p, t) => t('login.localTitle'),
  },
  {
    path: '/login',
    title: (_p, t) => t('login.title'),
  },
  {
    path: '/',
    title: (_p, t) => t('documentTitle.routes.home'),
  },
];

export const DOCUMENT_TITLE_ROUTE_PATTERNS = ROUTE_TITLE_RULES.map((rule) => rule.path);

export function resolveRouteDocumentTitle(pathname: string, t: TFunction): string {
  for (const rule of ROUTE_TITLE_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.title(match.params, t);
    }
  }
  return t('documentTitle.routes.default');
}

export function classroomDocumentTitle(name: string, t: TFunction): string {
  return t('documentTitle.dynamic.classroom', { name });
}

export function studentWorkDocumentTitle(studentName: string, t: TFunction): string {
  return t('documentTitle.dynamic.studentWork', { name: studentName });
}
